import { ICol } from "../../../../interfaces";

/**
 * Calculate the maximum depth of a header structure.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns The maximum depth of the header structure.
 */
export const calculateHeaderDepth = (headers: ICol[]): number => {
  let maxDepth = 1;

  for (const header of headers) {
    if (header.cols && header.cols.length > 0) {
      const subDepth = 1 + calculateHeaderDepth(header.cols);
      maxDepth = Math.max(maxDepth, subDepth);
    }
  }

  return maxDepth;
};

/**
 * Count the total leaf columns in a nested structure.
 * Space cells count as 1 column width.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns The total number of leaf columns in the header structure.
 */
export const countLeafColumns = (headers: ICol[]): number => {
  let count = 0;

  for (const header of headers) {
    if (header.type === "space") {
      count += 1; // Space always counts as 1 column
    } else if (header.cols && header.cols.length > 0) {
      count += countLeafColumns(header.cols);
    } else {
      count += 1;
    }
  }

  return count;
};

export interface ICollapsedColumnRange {
  start: number;
  end: number;
}

/**
 * Flat leaf-column ranges [start, end) currently collapsed via the header
 * column-collapse feature, derived from `headers` alone so row rendering
 * never needs collapsed state stored on individual cells.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns Flat leaf-column ranges currently collapsed.
 */
export const getCollapsedColumnRanges = (
  headers: ICol[],
): ICollapsedColumnRange[] => {
  const ranges: ICollapsedColumnRange[] = [];
  let pos = 0;

  for (const header of headers) {
    const span = countLeafColumns([header]);
    if (header.type === "split" && header.collapsed) {
      ranges.push({ start: pos, end: pos + span });
    }
    pos += span;
  }

  return ranges;
};
